import * as moment from 'moment';
import { Component } from '@angular/core';
import { ActionSheetController, AlertController, ModalController } from 'ionic-angular';

import { UserModel, WorkoutModel } from '../../../models/models';
import { ConfigurationProvider, UserProvider, WorkoutProvider } from '../../../providers/providers';
import { DashboardWorkoutPage } from './dashboard-workout';

@Component({
  selector: 'page-dashboard-exercise',
  templateUrl: 'dashboard-exercise.html'
})
export class DashboardExercisePage {
  isCurMonth: boolean = true;
  isLoaded: boolean = false;
  calDate: string;
  calendarDays: any = [];
  startDate: moment.Moment;
  todayDate: string;
  user: UserModel;

  constructor(
    private actionsheetCtrl: ActionSheetController,
    private alertCtrl: AlertController,
    private config: ConfigurationProvider, 
    private modalCtrl: ModalController,
    private userProvider: UserProvider,
    private workoutProvider: WorkoutProvider) {
  }

  doChangeDate(data) {
    this.doChangeDay(0, moment(`${data.month} 01 ${data.year}`));
  }

  doChangeDay(direction: number, newDate: moment.Moment = null) {
    if (!newDate) {
      newDate = direction === 0 ? moment() : moment(this.startDate).add(direction, 'month');
    }
    if (newDate.format('YYYY-MM') <= moment().format('YYYY-MM')) {
      this.isCurMonth = moment(newDate).format('YYYY-MM') >= moment().format('YYYY-MM');
      this.calDate = newDate.format('YYYY-MM-DD');
      this.startDate = newDate;
      this.loadCalendar();
    }
  }

  doOpenMenu(day, workout: WorkoutModel = new WorkoutModel()) {
    let buttons = [
      {
        text: 'Add Workout',
        role: '',
        icon: 'add',
        handler: () => this.menuEditWorkout(day, workout)
      }
    ];

    if (workout._id) {
      buttons = [
        {
          text: 'Edit',
          role: '',
          icon: 'create',
          handler: () => this.menuEditWorkout(day, workout)
        },
        {
          text: 'Copy Workout',
          role: '',
          icon: 'copy',
          handler: () => this.menuCopyWorkout(workout)
        },
        {
          text: 'Remove',
          role: 'destructive',
          icon: 'trash',
          handler: () => this.menuRemoveWorkout(day, workout)
        }
      ];
    }

    // action sheet
    let actionSheet = this.actionsheetCtrl.create({
      title: moment(workout.Date).format('ddd MMM DD, YYYY'),
      subTitle: workout._id ? workout.Name : null,
      buttons: buttons
    });
    actionSheet.present();
  }

  doSwipe(e) {
    this.doChangeDay(e.direction === 4 ? -1 : 1);
  }

  ionViewDidLoad() {
    this.startDate = moment();
    this.calDate = this.startDate.format('YYYY-MM-DD');
    this.todayDate = this.startDate.format('YYYY-MM-DD');
    this.user = this.userProvider.Get();
    this.loadView();
  }

  loadCalendar() {
    this.calendarDays = [];
    this.isLoaded = false;
    this.config.showLoading();

    const firstOfMonth = moment(this.startDate).startOf('month');
    const lastOfMonth   = moment(this.startDate).endOf('month');
    const calStart = moment(firstOfMonth).startOf('week');
    const calEnd = moment(lastOfMonth).isoWeekday(lastOfMonth.day() === 0 ? 13 : 6);

    this.workoutProvider.List(this.user._id, calStart.format('YYYY-MM-DD,'), calEnd.format('YYYY-MM-DD')).then((workouts: WorkoutModel[]) => {
      let row = [];
      for (var m = moment(calStart), i = 0; m.isBefore(calEnd); m.add(1, 'days'), i++) {
        // build day info
        let cls = '';
        let date = m.format('YYYY-MM-DD');
        let today = moment().format('YYYY-MM-DD');
        if (date === today) {
          cls = 'active';
        }
        else {
          if (date > today) {
            cls += ' future'
          }
          if (date > lastOfMonth.format('YYYY-MM-DD') || date < firstOfMonth.format('YYYY-MM-DD')) {
            cls += ' disabled'
          }
        }

        // check to push a new week
        if (i % 7 === 0) {
          this.calendarDays.push([]);
          row = this.calendarDays[this.calendarDays.length - 1];
        }

        // add day to the last week in the last
        row.push({ 
          'class': cls,
          'date': date, 
          'day': m.format('DD'),
          'workouts': workouts.filter(x => moment(x.Date).format('YYYY-MM-DD') === date)
        });
      }

      this.isLoaded = true;
      this.config.dismissLoading();
    });
  }

  loadView() {
    this.loadCalendar();
  }

  menuCopyWorkout(workout) {
    const prompt = this.alertCtrl.create({
      title: `${moment(workout.Date).format('ddd MMM DD, YYYY')} - ${workout.Name}`,
      message: 'Enter date for new workout.',
      inputs: [
        {
          name: 'date',
          type: 'date',
          placeholder: 'Date',
          value: moment().format('YYYY-MM-DD')
        }
      ],
      buttons: [
        {
          text: 'Cancel'
        },
        {
          text: 'OK',
          handler: data => {
            this.config.showLoading();
            this.workoutProvider.Load(workout).then((item: WorkoutModel) => {
              this.config.dismissLoading();
              // new workout
              let newWorkout = Object.assign(new WorkoutModel(), item);
              newWorkout._id = null;
              newWorkout.LastWorkoutId = item._id;
              newWorkout.Date = data.date;
              newWorkout.Time = moment().format('HH:mm');
              newWorkout.Notes = null;
              newWorkout.Exercises.map(exercise => {
                exercise._id = null;
                exercise.Sets = [{
                  _id: null,
                  Reps: null,
                  Weight: null,
                  SortOrder: 1
                }];
              });
              this.workoutProvider.SetMaxSets(newWorkout);
              // open modal
              this.menuEditWorkoutModal(newWorkout);
            });
          }
        }
      ]
    });
    prompt.present();
  }

  menuEditWorkout(day, workout: WorkoutModel = new WorkoutModel()) {
    this.config.showLoading();
    this.workoutProvider.Load(workout).then((item: WorkoutModel) => {
      item.UserId = this.user._id;
      item.Date  = day.date;
      if (item.LastWorkoutId) {
        item.LastWorkout = { _id: item.LastWorkoutId } as WorkoutModel;
        this.workoutProvider.Load(item.LastWorkout).then(() => {
          this.config.dismissLoading();
          this.menuEditWorkoutModal(item);
        });
      }
      else {
        this.config.dismissLoading();
        this.menuEditWorkoutModal(item);
      }
    });
  }

  menuEditWorkoutModal(workout) {
    let modal = this.modalCtrl.create(DashboardWorkoutPage, { 
      workout: workout
    });
    modal.present();
    modal.onDidDismiss(data => {
      if (data && data.saved && data.name) {
        this.config.showToast(`Workout saved for '${data.name}'`);
        this.calendarDays.map(weeks => {
          weeks.map(day => {
            // remove first
            day.workouts = day.workouts.filter(x => x._id !== workout._id);
            // add
            if (moment(day.date).format('YYYY-MM-DD') === moment(workout.Date).format('YYYY-MM-DD') && day.workouts.find(x => x._id === workout._id) === undefined) {
              day.workouts.push(workout);
            }
          });
        });
      }
    });
  }

  menuRemoveWorkout(day, workout) {
    let confirm = this.alertCtrl.create({
      title: `${moment(workout.Date).format('ddd MMM DD, YYYY')} - ${workout.Name}`,
      message: 'Are you sure?',
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Remove',
          handler: () => {
            this.config.showLoading();
            this.workoutProvider.Delete(workout._id).then(res => {
              this.config.dismissLoading();
              day.workouts = day.workouts.filter(x => x._id !== workout._id);
            });
          }
        }
      ]
    });
    confirm.present();
  }

}


// WEBPACK FOOTER //
// ./src/pages/dashboard/exercise/dashboard-exercise.ts